// =================================================================================================
// Template USAGE:
//  -- 3 files (WidgetTemplate, WidgetTemplateAsync, WidgetTemplateStructs)
//  - Update paths of internal components
//  - Replace WidgetTemplate to end widget name
//  - Update WidgetTemplateData with widget settings
//  - Update WidgetTemplateSettings with mapping of this settings in Settings Group
//  - Update ports and things information
//  - Set default values for widget data
//  - Register the widget to factory
//  - Add widget to index.ts file to be pe part of your app.js
// =================================================================================================

// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';

import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import * as WidgetsThingsStructs from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';

import { LazyLoadingAnimation } from '@Primitive/Lazy';

import { PortKeys, DeviceHierarchyData } from './DeviceHierarchyStructs';

// ------------------------------------------------------------------------------------------------------
const DeviceHierarchy = React.lazy(() => import("./DeviceHierarchyAsync"));
const DeviceHierarchySettings = React.lazy(() => import("./DeviceHierarchyAsync").then(module => ({ default: module.DeviceHierarchySettings })));
// =================================================================================================
export class DeviceHierarchyWidget extends IWidgetThingsClass<DeviceHierarchyData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <DeviceHierarchy />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <DeviceHierarchySettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: DeviceHierarchyData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'PortKeyDescription',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data))
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as DeviceHierarchyData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): DeviceHierarchyData {
        return {
            // TODO: Set the default values for the widget
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return DeviceHierarchyWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Device Hierarchy',
            WidgetTitle: '',
            Key: 'DeviceHierarchyWidget',
            Type: 'DeviceHierarchyWidget',
            Data: DeviceHierarchyWidget.GetDefaultWidgetDataStatic(),
            Description: 'DeviceHierarchyWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: DeviceHierarchyData): DeviceHierarchyData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}